import { useState, useMemo } from "react";
import { FullscreenLoader } from "@/components/FullscreenLoader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useSearchParamState, useSearchParamNumber } from "@/hooks/useSearchParamState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  Send,
  Copy,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw,
  Search,
  ArrowUpDown,
  List,
  LayoutList,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import type { Activity } from "@/types/activity";
import { ItemMetadata } from "@/components/activities/ItemMetadata";
import { PageHelpButton } from "@/components/PageHelpButton";

interface ActivityGroup {
  rootId: string;
  title: string;
  latestPublished: Activity | null;
  draft: Activity | null;
  publishedVersions: Activity[];
  newestUpdatedAt: string;
  newestCreatedAt: string;
}

function groupActivities(activities: Activity[]): ActivityGroup[] {
  const map = new Map<string, Activity[]>();

  for (const a of activities) {
    const rootId = a.activity_root_id || a.id;
    if (!map.has(rootId)) map.set(rootId, []);
    map.get(rootId)!.push(a);
  }

  const groups: ActivityGroup[] = [];
  for (const [rootId, items] of map) {
    items.sort((a, b) => b.version - a.version);
    const published = items.filter((a) => a.is_published);
    const draft = items.find((a) => !a.is_published) || null;
    const latestPublished = published.length > 0 ? published[0] : null;
    const display = draft || latestPublished!;

    groups.push({
      rootId,
      title: items[0].title,
      latestPublished,
      draft,
      publishedVersions: published,
      newestUpdatedAt: display.updated_at,
      newestCreatedAt: display.created_at,
    });
  }

  return groups;
}

type ViewMode = "detailed" | "compact";
type SortField = "updated_at" | "created_at";
type SortDir = "desc" | "asc";
type StatusFilter = "all" | "draft" | "published";

export default function Activities() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<{
    type: "publish" | "delete" | "delete-version" | "unpublish";
    activity: Activity;
  } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Toolbar state (persisted in URL for back-navigation)
  const [search, setSearch] = useSearchParamState("q", "");
  const [statusFilter, setStatusFilter] = useSearchParamState("status", "all") as [StatusFilter, (v: string) => void];
  const [sortField, setSortField] = useSearchParamState("sort", "updated_at") as [SortField, (v: string) => void];
  const [sortDir, setSortDir] = useSearchParamState("dir", "desc") as [SortDir, (v: string) => void];
  const [page, setPage] = useSearchParamNumber("page", 1);
  const [pageSize, setPageSizeRaw] = useSearchParamNumber("size", Number(localStorage.getItem("activities-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeRaw(v); localStorage.setItem("activities-page-size", String(v)); };
  const [viewMode, setViewModeState] = useState<ViewMode>(() => (localStorage.getItem("activities-view-mode") as ViewMode) || "detailed");
  const setViewMode = (v: ViewMode) => { setViewModeState(v); localStorage.setItem("activities-view-mode", v); };

  const { data: activities, isLoading } = useQuery({
    queryKey: ["activities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activity")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Activity[];
    },
  });

  // Map activity_id -> list of path titles it belongs to
  const { data: pathActivityLinks } = useQuery({
    queryKey: ["path-activity-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("path_activity")
        .select("activity_id, path:path_id(id, title, path_type, is_published, version, path_root_id)");
      if (error) throw error;
      return data as { activity_id: string; path: { id: string; title: string; path_type: string; is_published: boolean; version: number; path_root_id: string | null } }[];
    },
  });

  const activityPathsMap = useMemo(() => {
    const map = new Map<string, { title: string; pathType: string }[]>();
    if (!pathActivityLinks) return map;
    // Keep only latest published version per path_root_id
    const latestByRoot = new Map<string, { id: string; title: string; path_type: string; version: number; path_root_id: string | null; activityIds: string[] }>();
    for (const link of pathActivityLinks) {
      const p = link.path;
      if (!p || !p.is_published) continue;
      const rootId = p.path_root_id || p.id;
      const existing = latestByRoot.get(rootId);
      if (!existing || p.version > existing.version) {
        latestByRoot.set(rootId, { ...p, path_root_id: p.path_root_id, activityIds: [link.activity_id] });
      } else if (p.version === existing.version) {
        existing.activityIds.push(link.activity_id);
      }
    }
    // Also include drafts (unpublished paths)
    for (const link of pathActivityLinks) {
      const p = link.path;
      if (!p || p.is_published) continue;
      const rootId = p.path_root_id || p.id;
      const existing = latestByRoot.get(rootId);
      if (!existing) {
        latestByRoot.set(rootId, { ...p, path_root_id: p.path_root_id, activityIds: [link.activity_id] });
      } else {
        existing.activityIds.push(link.activity_id);
      }
    }
    for (const entry of latestByRoot.values()) {
      for (const actId of entry.activityIds) {
        const arr = map.get(actId) || [];
        if (!arr.some(a => a.title === entry.title)) {
          arr.push({ title: entry.title, pathType: entry.path_type });
        }
        map.set(actId, arr);
      }
    }
    return map;
  }, [pathActivityLinks]);

  const allGroups = useMemo(
    () => (activities ? groupActivities(activities) : []),
    [activities]
  );

  // Filter, search, sort
  const filteredGroups = useMemo(() => {
    let result = allGroups;

    if (statusFilter === "draft") {
      result = result.filter((g) => g.draft != null);
    } else if (statusFilter === "published") {
      result = result.filter((g) => g.latestPublished != null && !g.draft);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((g) => g.title.toLowerCase().includes(q));
    }

    result = [...result].sort((a, b) => {
      const aVal = sortField === "updated_at" ? a.newestUpdatedAt : a.newestCreatedAt;
      const bVal = sortField === "updated_at" ? b.newestUpdatedAt : b.newestCreatedAt;
      return sortDir === "desc" ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });

    return result;
  }, [allGroups, statusFilter, search, sortField, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / pageSize));
  const safeCurrentPage = Math.min(page, totalPages);
  const paginatedGroups = filteredGroups.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  const handleSearchChange = (v: string) => { setSearch(v); setPage(1); };
  const handleStatusChange = (v: StatusFilter) => { setStatusFilter(v); setPage(1); };
  const handleSortToggle = () => {
    if (sortDir === "desc") {
      if (sortField === "updated_at") {
        setSortDir("asc");
      } else {
        setSortField("updated_at");
        setSortDir("desc");
      }
    } else {
      if (sortField === "updated_at") {
        setSortField("created_at");
        setSortDir("desc");
      } else {
        setSortDir("asc");
      }
    }
  };

  const toggleGroup = (rootId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  const publishMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      const { error } = await supabase
        .from("activity")
        .update({ is_published: true, updated_by: user?.id })
        .eq("id", activity.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Activité publiée");
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la publication"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      const { data: stepIds } = await supabase
        .from("activity_step")
        .select("id")
        .eq("activity_id", activity.id);
      if (stepIds && stepIds.length > 0) {
        await supabase
          .from("activity_step_transition")
          .delete()
          .in("from_activity_step_id", stepIds.map((s) => s.id));
      }
      await supabase.from("activity_step").delete().eq("activity_id", activity.id);
      const { error } = await supabase.from("activity").delete().eq("id", activity.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Brouillon supprimé");
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const deleteVersionMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      const { data: stepIds } = await supabase.from("activity_step").select("id, card_id, card_group_id").eq("activity_id", activity.id);
      const sIds = stepIds?.map((s) => s.id) || [];
      const cardIds = stepIds?.map((s) => s.card_id).filter(Boolean) as string[];
      const groupIds = stepIds?.map((s) => s.card_group_id).filter(Boolean) as string[];

      if (sIds.length > 0) await supabase.from("activity_step_transition").delete().in("from_activity_step_id", sIds);
      if (groupIds.length > 0) {
        const { data: groupItems } = await supabase.from("card_group_item").select("id, card_id").in("card_group_id", groupIds);
        const groupCardIds = groupItems?.map((gi) => gi.card_id) || [];
        await supabase.from("card_group_item").delete().in("card_group_id", groupIds);
        if (groupCardIds.length > 0) await supabase.from("card").delete().in("id", groupCardIds);
      }
      if (sIds.length > 0) await supabase.from("activity_step").delete().in("id", sIds);
      if (cardIds.length > 0) await supabase.from("card").delete().in("id", cardIds);
      if (groupIds.length > 0) await supabase.from("card_group").delete().in("id", groupIds);

      await supabase.from("path_activity").delete().eq("activity_id", activity.id);
      const { error } = await supabase.from("activity").delete().eq("id", activity.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Version supprimée");
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la suppression de la version"),
  });

  const unpublishMutation = useMutation({
    mutationFn: async (activity: Activity) => {
      const { error } = await supabase.from("activity").update({ is_published: false, updated_by: user?.id }).eq("id", activity.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Activité repassée en brouillon");
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors du passage en brouillon"),
  });

  const [versionLoading, setVersionLoading] = useState(false);

  const createNewVersion = async (activity: Activity) => {
    if (!user) return;
    setVersionLoading(true);
    try {
      const { data: newActivity, error } = await supabase
        .from("activity")
        .insert([{
          title: activity.title,
          summary: activity.summary,
          estimated_duration_minutes: activity.estimated_duration_minutes,
          metadata: activity.metadata,
          activity_root_id: activity.activity_root_id || activity.id,
          version: activity.version + 1,
          is_published: false,
          created_by: user.id,
          updated_by: user.id,
        }])
        .select()
        .single();
      if (error) throw error;

      const { data: oldSteps } = await supabase
        .from("activity_step")
        .select("*")
        .eq("activity_id", activity.id);

      if (oldSteps && oldSteps.length > 0) {
        const oldToNew = new Map<string, string>();
        for (const step of oldSteps) {
          const { data: newStep } = await supabase.from("activity_step").insert([{
            activity_id: newActivity.id,
            card_id: step.card_id,
            card_group_id: step.card_group_id,
          }]).select("id").single();
          if (newStep) oldToNew.set(step.id, newStep.id);
        }

        for (const oldStep of oldSteps) {
          const { data: transitions } = await supabase
            .from("activity_step_transition")
            .select("*")
            .eq("from_activity_step_id", oldStep.id);
          if (transitions) {
            for (const t of transitions) {
              const newFrom = oldToNew.get(t.from_activity_step_id);
              const newTo = oldToNew.get(t.to_activity_step_id);
              if (newFrom && newTo) {
                await supabase.from("activity_step_transition").insert({
                  from_activity_step_id: newFrom,
                  to_activity_step_id: newTo,
                  condition: t.condition,
                  order_index: t.order_index,
                });
              }
            }
          }
        }

        // Remap metadata.nodePositions and first_step_id
        const updates: Record<string, unknown> = {};

        if (activity.first_step_id && oldToNew.has(activity.first_step_id)) {
          updates.first_step_id = oldToNew.get(activity.first_step_id);
        }

        const meta = (activity.metadata as Record<string, unknown>) || {};
        if (meta.nodePositions && typeof meta.nodePositions === "object") {
          const oldPositions = meta.nodePositions as Record<string, { x: number; y: number }>;
          const newPositions: Record<string, { x: number; y: number }> = {};
          for (const [oldId, pos] of Object.entries(oldPositions)) {
            const newId = oldToNew.get(oldId);
            if (newId) newPositions[newId] = pos;
          }
          updates.metadata = { ...meta, nodePositions: newPositions };
        }

        if (Object.keys(updates).length > 0) {
          await supabase
            .from("activity")
            .update(updates)
            .eq("id", newActivity.id);
        }
      }

      toast.success(`Nouvelle version (v${newActivity.version}) créée`);
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      navigate(`/activities/${newActivity.id}`);
    } catch {
      toast.error("Erreur lors de la création de la nouvelle version");
    } finally {
      setVersionLoading(false);
    }
  };

  const sortLabel = sortField === "updated_at"
    ? `MAJ ${sortDir === "desc" ? "↓" : "↑"}`
    : `Création ${sortDir === "desc" ? "↓" : "↑"}`;

  return (
    <>
    {versionLoading && <FullscreenLoader message="Création de la nouvelle version en cours…" />}
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-tour="activities-title">Activités</h1>
          <p className="mt-1 text-muted-foreground">
            Gérez les activités et leurs versions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PageHelpButton tourKey="activities" />
          <Button onClick={() => navigate("/activities/new")} data-tour="activities-create">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle activité
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3" data-tour="activities-toolbar">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher une activité…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => handleStatusChange(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="draft">Brouillons</SelectItem>
            <SelectItem value="published">Publiés</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleSortToggle} className="gap-1.5">
          <ArrowUpDown className="h-3.5 w-3.5" />
          {sortLabel}
        </Button>
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center border border-border rounded-md">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={viewMode === "compact" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-r-none"
                  onClick={() => setViewMode("compact")}
                >
                  <List className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Vue compacte</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={viewMode === "detailed" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-l-none"
                  onClick={() => setViewMode("detailed")}
                >
                  <LayoutList className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Vue détaillée</p></TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : paginatedGroups.length > 0 ? (
        <>
          <div className="grid gap-3">
            {paginatedGroups.map((group) => (
              <ActivityGroupCard
                key={group.rootId}
                group={group}
                isSuperAdmin={isSuperAdmin}
                isExpanded={expandedGroups.has(group.rootId)}
                activityPaths={(() => {
                  const allIds = [
                    ...(group.draft ? [group.draft.id] : []),
                    ...group.publishedVersions.map(v => v.id),
                  ];
                  const merged: { title: string; pathType: string }[] = [];
                  for (const id of allIds) {
                    for (const p of activityPathsMap.get(id) || []) {
                      if (!merged.some(m => m.title === p.title)) merged.push(p);
                    }
                  }
                  return merged;
                })()}
                viewMode={viewMode}
                onToggle={() => toggleGroup(group.rootId)}
                onNavigate={(id) => navigate(`/activities/${id}`)}
                onPublish={(a) => setConfirmAction({ type: "publish", activity: a })}
                onDelete={(a) => setConfirmAction({ type: "delete", activity: a })}
                onDeleteVersion={(a) => setConfirmAction({ type: "delete-version", activity: a })}
                onUnpublish={(a) => setConfirmAction({ type: "unpublish", activity: a })}
                onNewVersion={createNewVersion}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{filteredGroups.length} résultat{filteredGroups.length > 1 ? "s" : ""}</span>
              <span>·</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span>par page</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safeCurrentPage <= 1} onClick={() => setPage(safeCurrentPage - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-sm text-muted-foreground">
                {safeCurrentPage} / {totalPages}
              </span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safeCurrentPage >= totalPages} onClick={() => setPage(safeCurrentPage + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Card className="border-border/50 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">
              {search || statusFilter !== "all" ? "Aucune activité ne correspond aux filtres" : "Aucune activité pour le moment"}
            </p>
            {!search && statusFilter === "all" && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/activities/new")}
              >
                <Plus className="mr-2 h-4 w-4" />
                Créer votre première activité
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm dialog */}
      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "publish" && "Publier cette activité ?"}
              {confirmAction?.type === "delete" && "Supprimer ce brouillon ?"}
              {confirmAction?.type === "delete-version" && `Supprimer la version v${confirmAction.activity.version} ?`}
              {confirmAction?.type === "unpublish" && `Repasser la v${confirmAction.activity.version} en brouillon ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "publish" && "Une activité publiée devient immuable. Vous devrez créer une nouvelle version pour la modifier."}
              {confirmAction?.type === "delete" && "Cette action est irréversible. Le brouillon et ses étapes seront définitivement supprimés."}
              {confirmAction?.type === "delete-version" && "Cette action est irréversible. La version publiée et toutes ses dépendances (étapes, cartes, etc.) seront définitivement supprimées."}
              {confirmAction?.type === "unpublish" && "L'activité repassera en brouillon et sera modifiable."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmAction?.type === "delete" || confirmAction?.type === "delete-version"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "publish") publishMutation.mutate(confirmAction.activity);
                else if (confirmAction.type === "delete") deleteMutation.mutate(confirmAction.activity);
                else if (confirmAction.type === "delete-version") deleteVersionMutation.mutate(confirmAction.activity);
                else if (confirmAction.type === "unpublish") unpublishMutation.mutate(confirmAction.activity);
              }}
            >
              {confirmAction?.type === "publish" && "Publier"}
              {confirmAction?.type === "delete" && "Supprimer"}
              {confirmAction?.type === "delete-version" && "Supprimer"}
              {confirmAction?.type === "unpublish" && "Repasser en brouillon"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
}

/* ─── Activity Group Card ────────────────────────────────── */

interface ActivityGroupCardProps {
  group: ActivityGroup;
  isSuperAdmin: boolean;
  isExpanded: boolean;
  activityPaths: { title: string; pathType: string }[];
  viewMode: ViewMode;
  onToggle: () => void;
  onNavigate: (id: string) => void;
  onPublish: (a: Activity) => void;
  onDelete: (a: Activity) => void;
  onDeleteVersion: (a: Activity) => void;
  onUnpublish: (a: Activity) => void;
  onNewVersion: (a: Activity) => void;
}

function ActivityGroupCard({
  group,
  isSuperAdmin,
  isExpanded,
  activityPaths,
  viewMode,
  onToggle,
  onNavigate,
  onPublish,
  onDelete,
  onDeleteVersion,
  onUnpublish,
  onNewVersion,
}: ActivityGroupCardProps) {
  const displayActivity = group.draft || group.latestPublished!;
  const hasVersions = group.publishedVersions.length > 0;
  const isCompact = viewMode === "compact";

  const dropdownMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {group.draft && (
          <DropdownMenuItem onClick={() => onNavigate(group.draft!.id)}>
            <Pencil className="mr-2 h-4 w-4" />
            Modifier le brouillon
          </DropdownMenuItem>
        )}
        {group.draft && (
          <DropdownMenuItem onClick={() => onPublish(group.draft!)}>
            <Send className="mr-2 h-4 w-4" />
            Publier le brouillon
          </DropdownMenuItem>
        )}
        {group.latestPublished && !group.draft && (
          <DropdownMenuItem onClick={() => onNewVersion(group.latestPublished!)}>
            <Copy className="mr-2 h-4 w-4" />
            Nouvelle version
          </DropdownMenuItem>
        )}
        {group.latestPublished && (
          <DropdownMenuItem onClick={() => onNavigate(group.latestPublished!.id)}>
            <Eye className="mr-2 h-4 w-4" />
            Voir la dernière version publiée
          </DropdownMenuItem>
        )}
        {isSuperAdmin && group.latestPublished && !group.draft && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onUnpublish(group.latestPublished!)}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Repasser en brouillon
            </DropdownMenuItem>
          </>
        )}
        {hasVersions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onToggle}>
              <ChevronDown className="mr-2 h-4 w-4" />
              {isExpanded ? "Masquer les versions" : "Voir toutes les versions"}
            </DropdownMenuItem>
          </>
        )}
        {group.draft && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onDelete(group.draft!)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Supprimer le brouillon
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const versionHistory = isExpanded && hasVersions && (
    <CardContent className="pt-0 pb-4">
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground mb-2">
          Historique des versions
        </p>
        {group.publishedVersions.map((v, index) => (
          <div
            key={v.id}
            className="flex items-center justify-between px-3 py-2 rounded-md border border-border/50 hover:bg-accent/30 cursor-pointer transition-colors"
            onClick={() => onNavigate(v.id)}
          >
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">v{v.version}</Badge>
              <span className="text-sm">{v.title}</span>
            </div>
            <ItemMetadata
              createdAt={v.created_at}
              createdBy={v.created_by}
              updatedAt={v.updated_at}
              updatedBy={v.updated_by}
              compact
            />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{new Date(v.created_at).toLocaleDateString("fr-FR")}</span>
              {isSuperAdmin ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => onNavigate(v.id)}>
                      <Eye className="mr-2 h-4 w-4" />Voir cette version
                    </DropdownMenuItem>
                    {index === 0 && !group.draft && (
                      <DropdownMenuItem onClick={() => onUnpublish(v)}>
                        <RotateCcw className="mr-2 h-4 w-4" />Repasser en brouillon
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => onDeleteVersion(v)}>
                      <Trash2 className="mr-2 h-4 w-4" />Supprimer cette version
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  );

  if (isCompact) {
    return (
      <Card className="border-border/50">
        <CardHeader className="py-2 px-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium truncate min-w-0 flex-1">{group.title}</span>
            {activityPaths.length > 0 && (
              <div className="flex flex-wrap gap-1 shrink-0">
                {activityPaths.map((p) => (
                  <Badge key={p.title} variant="outline" className="text-[10px] font-normal py-0">
                    {p.title}
                  </Badge>
                ))}
              </div>
            )}
            {displayActivity.estimated_duration_minutes != null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <Clock className="h-3 w-3" />
                {displayActivity.estimated_duration_minutes} min
              </div>
            )}
            <ItemMetadata
              createdAt={displayActivity.created_at}
              createdBy={displayActivity.created_by}
              updatedAt={displayActivity.updated_at}
              updatedBy={displayActivity.updated_by}
              compact
            />
            {group.latestPublished && (
              <Badge variant="default" className="text-[10px] py-0">
                v{group.latestPublished.version}
              </Badge>
            )}
            {group.draft && (
              <Badge variant="secondary" className="text-[10px] py-0">
                Brouillon v{group.draft.version}
              </Badge>
            )}
            {dropdownMenu}
          </div>
        </CardHeader>
        {versionHistory}
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="py-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{group.title}</CardTitle>
            </div>
            {displayActivity.summary && (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
                {displayActivity.summary}
              </p>
            )}
            {activityPaths.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {activityPaths.map((p) => (
                  <Badge key={p.title} variant="outline" className="text-xs font-normal">
                    {p.title}
                  </Badge>
                ))}
              </div>
            )}
            <ItemMetadata
              createdAt={displayActivity.created_at}
              createdBy={displayActivity.created_by}
              updatedAt={displayActivity.updated_at}
              updatedBy={displayActivity.updated_by}
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {displayActivity.estimated_duration_minutes != null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {displayActivity.estimated_duration_minutes} min
              </div>
            )}
            {group.latestPublished && (
              <Badge variant="default">
                Publiée v{group.latestPublished.version}
              </Badge>
            )}
            {group.draft && (
              <Badge variant="secondary">
                Brouillon v{group.draft.version}
              </Badge>
            )}
            {dropdownMenu}
          </div>
        </div>
      </CardHeader>
      {versionHistory}
    </Card>
  );
}
